import { Box, Chip, Stack, Typography } from '@mui/material';
import VerifiedRoundedIcon from '@mui/icons-material/VerifiedRounded';
import ScheduleRoundedIcon from '@mui/icons-material/ScheduleRounded';
import { readReport } from '@/features/candleCard/reportAdapters';

/**
 * One report, as the client sees it.
 *
 * Each bot speaks its own language and this tile lets it: symmetry for botox
 * planning, youthfulness for a lift, harmony for filler or a profile. The
 * figures are never blended, never renamed into a house metric, and never
 * presented as a verdict on the person — the heading says what was measured.
 *
 * Everything shown comes from the adapter's view-model, so this file has no
 * idea which bot produced the numbers. That is the point: a bot can change
 * without this component changing.
 */

/** The review state, which is the most consequential thing on the tile.
 *  Preliminary figures are shown — the client asked for that — but never
 *  without saying a doctor has not looked yet. */
const STATE = {
  reviewed: { color: '#2E7D55', Icon: VerifiedRoundedIcon },
  preliminary: { color: '#C0872E', Icon: ScheduleRoundedIcon },
};

export default function ReadingTile({ report, t, lang, onOpen }) {
  const view = readReport(report, t);
  const state = STATE[report.state] || STATE.preliminary;
  const { Icon } = state;
  const fmt = (n) => new Intl.NumberFormat(lang === 'fa' ? 'fa-IR' : 'en-GB').format(n);
  const date = report.at
    ? new Date(report.at).toLocaleDateString(lang === 'fa' ? 'fa-IR' : 'en-GB',
      { year: 'numeric', month: 'long', day: 'numeric' })
    : null;

  const pct = view.headline && view.headline.max
    ? Math.max(0, Math.min(100, (view.headline.value / view.headline.max) * 100))
    : null;

  return (
    <Box
      component="button"
      type="button"
      onClick={onOpen}
      sx={{
        font: 'inherit', textAlign: 'start', width: '100%', cursor: 'pointer',
        p: 2, borderRadius: 3,
        border: '1px solid', borderColor: 'divider',
        // Candle White on the porcelain sheet. Painting these #F7F3EC made
        // them the same value as the page and they read as one flat wash.
        bgcolor: '#FFFFFF',
        display: 'block',
        '&:hover': { borderColor: 'rgba(192,167,134,.8)' },
      }}
    >
      <Stack direction="row" alignItems="flex-start" justifyContent="space-between"
        spacing={1.5}>
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontWeight: 700, fontSize: 15, lineHeight: 1.3 }} noWrap>
            {report.serviceName || t('candleCard.reading.untitled')}
          </Typography>
          {date && (
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>{date}</Typography>
          )}
        </Box>
        <Stack direction="row" spacing={.5} alignItems="center"
          sx={{ color: state.color, flexShrink: 0 }}>
          <Icon sx={{ fontSize: 17 }} />
          <Typography variant="caption" sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>
            {report.state === 'reviewed'
              ? t('candleCard.state.reviewed')
              : t('candleCard.state.preliminary')}
          </Typography>
        </Stack>
      </Stack>

      {view.headline ? (
        <Box sx={{ mt: 1.75 }}>
          <Stack direction="row" alignItems="baseline" spacing={.75}>
            <Typography sx={{ fontSize: 34, fontWeight: 800, lineHeight: 1,
              color: '#11223E' }}>
              {fmt(view.headline.value)}
            </Typography>
            {view.headline.unit && (
              <Typography sx={{ fontSize: 17, fontWeight: 700, color: '#11223E' }}>
                {view.headline.unit}
              </Typography>
            )}
            <Typography variant="body2" sx={{ color: 'text.secondary', pb: .25 }}>
              {view.headline.label}
            </Typography>
          </Stack>
          {pct != null && (
            // A measure bar, not a ring: it reads at a glance on a phone and
            // does not imply a target the person has failed to reach.
            <Box aria-hidden="true" sx={{ mt: 1, height: 7, borderRadius: 4,
              bgcolor: 'rgba(17,34,62,.09)', overflow: 'hidden' }}>
              <Box sx={{ width: `${pct}%`, height: '100%', borderRadius: 4,
                background: 'linear-gradient(90deg,#C0A786,#8C6D1F)' }} />
            </Box>
          )}
        </Box>
      ) : (
        <Typography variant="body2" sx={{ mt: 1.5, color: 'text.secondary' }}>
          {t('candleCard.reading.noFigures')}
        </Typography>
      )}

      {view.stats.length > 0 && (
        <Stack direction="row" spacing={2.5} sx={{ mt: 1.75, flexWrap: 'wrap' }} useFlexGap>
          {view.stats.map((s) => (
            <Box key={s.label}>
              <Typography sx={{ fontWeight: 700, fontSize: 15, color: '#11223E' }}>
                {/* Numbers are formatted HERE, not in the adapter: Persian
                    readers get Persian numerals, and a bot descriptor has no
                    business knowing which language the page is in. */}
                {typeof s.value === 'number' ? `${fmt(s.value)}${s.unit || ''}` : s.text}
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                {s.label}
              </Typography>
            </Box>
          ))}
        </Stack>
      )}

      {view.zones.length > 0 && (
        <Stack direction="row" spacing={.75} sx={{ mt: 1.75, flexWrap: 'wrap' }} useFlexGap>
          {view.zones.map((z) => (
            // Anatomical names arrive from the bots in English and stay that
            // way: it is how clinicians say them in Persian conversation too.
            <Chip key={z} label={z} size="small" dir="ltr"
              sx={{ bgcolor: 'rgba(17,34,62,.06)', fontWeight: 600 }} />
          ))}
        </Stack>
      )}

      {report.state === 'reviewed' && report.physicianName && (
        <Typography variant="caption"
          sx={{ display: 'block', mt: 1.5, color: 'text.secondary' }}>
          {t('candleCard.reading.by', { name: report.physicianName })}
        </Typography>
      )}
    </Box>
  );
}
